import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ShieldCheck, ShieldAlert, CheckCircle2, Clock, AlertCircle, Plus, CalendarIcon, CircleCheck } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  checkConditionExpiry,
  getConditionStatus,
  getStatusText,
  getExpiryDisplayText,
  type ApprovalCondition,
} from "@/lib/approval-utils";

interface ApprovalConditionsProps {
  policyId: string;
  policyCreatedBy: string;
  assignedApprovers: string[];
}

export function ApprovalConditions({
  policyId,
  policyCreatedBy,
  assignedApprovers,
}: ApprovalConditionsProps) {
  const [conditions, setConditions] = useState<ApprovalCondition[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    expiry_date: null as Date | null,
  });
  const { profile, roles } = useAuth();
  const { toast } = useToast();

  const fetchConditions = useCallback(async () => {
    if (!profile?.company_id || !policyId) return;
    try {
      const { data, error } = await supabase
        .from("approval_conditions")
        .select("*")
        .eq("policy_id", policyId)
        .eq("company_id", profile.company_id)
        .order("created_at", { ascending: true });
      if (error) throw error;
      setConditions(data || []);
    } catch {
      toast({ title: "Error", description: "Failed to fetch approval conditions", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [policyId, profile?.company_id, toast]);

  useEffect(() => {
    if (!policyId || !profile?.company_id) return;
    void fetchConditions();

    const channel = supabase
      .channel(`approval-conditions-${policyId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "approval_conditions", filter: `policy_id=eq.${policyId}` }, () => {
        void fetchConditions();
      })
      .subscribe();

    return () => { void supabase.removeChannel(channel); };
  }, [fetchConditions, policyId, profile?.company_id]);

  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.description.trim() || !profile) return;
    try {
      const { error } = await supabase.from("approval_conditions").insert({
        policy_id: policyId,
        company_id: profile.company_id,
        created_by: profile.id,
        condition_type: "policy_level",
        title: formData.title.trim(),
        description: formData.description.trim(),
        expiry_date: formData.expiry_date?.toISOString().split("T")[0] || null,
      });
      if (error) throw error;
      toast({ title: "Condition added" });
      setIsDialogOpen(false);
      setFormData({ title: "", description: "", expiry_date: null });
      await fetchConditions();
    } catch {
      toast({ title: "Error", description: "Failed to create condition", variant: "destructive" });
    }
  };

  const handleApprove = async (condition: ApprovalCondition) => {
    if (!profile) return;
    try {
      const { error } = await supabase
        .from("approval_conditions")
        .update({ is_approved: true, approved_by: profile.id, approved_at: new Date().toISOString() })
        .eq("id", condition.id);
      if (error) throw error;
      toast({ title: "Condition marked as met" });
      await fetchConditions();
    } catch {
      toast({ title: "Error", description: "Failed to approve condition", variant: "destructive" });
    }
  };

  const canCreate =
    profile?.id === policyCreatedBy ||
    roles?.includes("approver") ||
    roles?.includes("reviewer") ||
    roles?.includes("admin");

  const isApprover = assignedApprovers.includes(profile?.id || "");

  const activeConditions = conditions.filter((c) => !checkConditionExpiry(c) && !c.is_approved);
  const expiredConditions = conditions.filter((c) => checkConditionExpiry(c) && !c.is_approved);
  const approvedConditions = conditions.filter((c) => c.is_approved);
  const pendingCount = activeConditions.length + expiredConditions.length;

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            {pendingCount === 0 && conditions.length > 0 ? (
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
            ) : pendingCount > 0 ? (
              <ShieldAlert className="w-4 h-4 text-destructive" />
            ) : (
              <ShieldCheck className="w-4 h-4" />
            )}
            Approval Conditions
            {conditions.length > 0 && (
              <span className="text-muted-foreground font-normal">({conditions.length})</span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {pendingCount > 0 && (
              <Badge variant="destructive" className="text-xs">{pendingCount} pending</Badge>
            )}
            {pendingCount === 0 && conditions.length > 0 && (
              <Badge variant="secondary" className="text-xs gap-1 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="w-3 h-3" />All met
              </Badge>
            )}
            {canCreate && (
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1">
                    <Plus className="w-3.5 h-3.5" />
                    Add
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Approval Condition</DialogTitle>
                    <DialogDescription>
                      Define a condition that must be met before this policy can be approved.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="space-y-1.5">
                      <Label>Title *</Label>
                      <Input
                        placeholder="e.g., Legal review completed"
                        value={formData.title}
                        onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Description *</Label>
                      <Textarea
                        placeholder="Describe what must be completed…"
                        value={formData.description}
                        onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Expiry Date (optional)</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn("w-full justify-start text-left font-normal", !formData.expiry_date && "text-muted-foreground")}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.expiry_date ? format(formData.expiry_date, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.expiry_date || undefined}
                            onSelect={(date) => setFormData((p) => ({ ...p, expiry_date: date || null }))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreate} disabled={!formData.title.trim() || !formData.description.trim()}>
                      Add Condition
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-primary" />
          </div>
        ) : conditions.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <ShieldCheck className="mx-auto mb-2 h-8 w-8 opacity-30" />
            <p className="text-sm">No approval conditions</p>
            <p className="text-xs mt-0.5">Add conditions that must be met before approval</p>
          </div>
        ) : (
          <div className="space-y-4 divide-y divide-border">
            {/* Active */}
            {activeConditions.map((condition) => {
              const status = getConditionStatus(condition);
              const expiryInfo = getExpiryDisplayText(condition);
              return (
                <div key={condition.id} className="group pt-4 first:pt-0">
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="text-sm font-medium leading-none">{condition.title}</span>
                        {status === "expiring_soon" && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-amber-500 text-amber-600 dark:text-amber-400">
                            Expiring soon
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{condition.description}</p>
                      {condition.expiry_date && (
                        <p className={cn("text-[10px] mt-1", expiryInfo.color)}>{expiryInfo.text}</p>
                      )}
                      {isApprover && (
                        <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                                <CircleCheck className="w-3 h-3" />
                                Mark as met
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Mark condition as met?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Confirm that "{condition.title}" has been satisfied.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => void handleApprove(condition)}>Confirm</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Expired */}
            {expiredConditions.map((condition) => {
              const expiryInfo = getExpiryDisplayText(condition);
              return (
                <div key={condition.id} className="group pt-4 first:pt-0">
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                      <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="text-sm font-medium leading-none">{condition.title}</span>
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">Expired</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{condition.description}</p>
                      <p className={cn("text-[10px] mt-1", expiryInfo.color)}>{expiryInfo.text}</p>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Approved */}
            {approvedConditions.map((condition) => (
              <div key={condition.id} className="pt-4 first:pt-0">
                <div className="flex items-start gap-2.5">
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 mb-0.5">
                      <span className="text-sm font-medium leading-none opacity-70">{condition.title}</span>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 gap-0.5 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 className="w-2.5 h-2.5" />Met
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed opacity-70">{condition.description}</p>
                    {condition.approved_at && (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(condition.approved_at), { addSuffix: true })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
